/**
 * App Exclusion API for Electron
 * Manages app exclusions for context collection
 */

import { ElectronAPI } from '../base/ElectronAPI';

import type {
  AppAccessMode,
  AppAccessPolicy,
  AppAccessPolicyWriteResult,
  AppPickerErrorCode,
} from '@electron/shared/types';

export interface AppWithExclusionState {
  name: string;
  path: string;
  icon?: string;
  displayName: string;
  identifier: string;
  isDefaultExclusion: boolean;
  canUserModify: boolean;
  isExcluded: boolean;
  lockedBy?: 'default' | 'team';
}

export interface DomainWithExclusionState {
  domain: string;
  canUserModify: boolean;
  lockedBy?: 'default' | 'team';
}

export class ExclusionAPI extends ElectronAPI {
  /**
   * Get all apps with their exclusion states
   */
  async getAppExclusions(): Promise<AppWithExclusionState[]> {
    return this.invoke<AppWithExclusionState[]>('get-app-exclusions');
  }

  async getAppAccessPolicy(): Promise<AppAccessPolicy> {
    return this.invoke<AppAccessPolicy>('get-app-access-policy');
  }

  async setAppAccessMode(
    mode: AppAccessMode
  ): Promise<AppAccessPolicyWriteResult> {
    try {
      return await this.invoke<AppAccessPolicyWriteResult>(
        'set-app-access-mode',
        mode
      );
    } catch (error) {
      console.error('Failed to set app access mode:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setAppExcluded(
    identifier: string,
    excluded: boolean
  ): Promise<AppAccessPolicyWriteResult> {
    try {
      return await this.invoke<AppAccessPolicyWriteResult>('set-app-excluded', {
        identifier,
        excluded,
      });
    } catch (error) {
      console.error('Failed to set app exclusion:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get all domain exclusions
   */
  async getDomainExclusions(): Promise<DomainWithExclusionState[]> {
    try {
      return await this.invoke<DomainWithExclusionState[]>(
        'get-domain-exclusions'
      );
    } catch (error) {
      console.error('Failed to get domain exclusions:', error);
      return [];
    }
  }

  /**
   * Get all user-included domains (overrides that keep a site captured even when
   * its category is excluded)
   */
  async getIncludedDomains(): Promise<DomainWithExclusionState[]> {
    try {
      return await this.invoke<DomainWithExclusionState[]>(
        'get-included-domains'
      );
    } catch (error) {
      console.error('Failed to get included domains:', error);
      return [];
    }
  }

  /**
   * Update user-included domains
   * @param includedDomains Array of domains to always include
   */
  async updateIncludedDomains(
    includedDomains: string[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      return await this.invoke<{ success: boolean; error?: string }>(
        'update-included-domains',
        includedDomains
      );
    } catch (error) {
      console.error('Failed to update included domains:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Update user domain exclusions
   * @param excludedDomains Array of domains to exclude
   */
  async updateDomainExclusions(
    excludedDomains: string[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      return await this.invoke<{ success: boolean; error?: string }>(
        'update-domain-exclusions',
        excludedDomains
      );
    } catch (error) {
      console.error('Failed to update domain exclusions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Reset user exclusions to defaults
   */
  async resetAppExclusions(): Promise<{ success: boolean; error?: string }> {
    try {
      return await this.invoke<{ success: boolean; error?: string }>(
        'reset-app-exclusions'
      );
    } catch (error) {
      console.error('Failed to reset app exclusions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Open dynamic app picker for exclusion
   */
  async openDynamicAppPicker() {
    try {
      return await this.invoke<{
        success: boolean;
        code?: AppPickerErrorCode;
        error?: string;
        data?: AppWithExclusionState[];
      }>('open-dynamic-app-picker');
    } catch (error) {
      console.error('Failed to open dynamic app picker:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}
